/* UTILS */
import router from "@/router"
import { setup, createActor, fromPromise } from "xstate"
import { globalActor } from "@/machines/globalMachine"
import { useCookies } from "@vueuse/integrations/useCookies.js"
import { COOKIES } from "@/config/constants"
import { type GlobalMachineEvent } from "@/machines/types"

/* HELPERS */
import { useSessionStorage } from "@/helpers/useSessionStorage"
import { useGotoParam } from "@/composables/useGoto"
import { usePendingUser } from "@/composables/usePendingUser"

/* STORES */
import { useUserStore } from "@/stores/user"
import { useAnswersStore } from "@/features/screening/store/answers.store"
import { storeToRefs } from "pinia"
import type { NextAssessmentData } from "@/types/next-assessment"
import { useConfigStore } from "@/stores/config"

const { removeGotoParam, isGotoParam } = useGotoParam()

type LoadScreeningDataResult =
  | { type: "consent_needed"; data?: NextAssessmentData }
  | { type: "redirected"; data?: NextAssessmentData }
  | { type: "assessment"; data?: NextAssessmentData }

const deleteSessionItem = () => {
  useSessionStorage().deleteSessionItem("origin")
  removeGotoParam()
}

// Loader for Screening datas
const loadScreeningData = fromPromise(
  async (): Promise<LoadScreeningDataResult> => {
    const userStore = useUserStore()
    const { fetchScreeningData } = useAnswersStore()
    const { isConsentNeeded } = usePendingUser()

    const { nextAssessment } = storeToRefs(userStore)

    if (isConsentNeeded.value) {
      return { type: "consent_needed" }
    }

    if (!nextAssessment.value) {
      await fetchScreeningData()
    }

    const assessmentData = nextAssessment.value?.next_assessment

    if (!nextAssessment.value || !assessmentData?.assessment) {
      deleteSessionItem()
      throw new Error("NO_NEXT_ASSESSMENT")
    }

    if (assessmentData?.assessment !== "screening_interview") {
      deleteSessionItem()

      return { type: "redirected", data: assessmentData }
    }

    return { type: "assessment", data: assessmentData }
  }
)

export const screeningMachine = setup({
  actors: {
    loadScreeningData,
  },
  types: {} as {
    events: any
    context: {
      errorSource?: string
    }
  },
  actions: {
    setErrorSource: ({ context }, params: { source: string }) => {
      context.errorSource = params.source
    },
  },
  guards: {
    checkGoToParam: () => isGotoParam("screening-authorize"), // Has goto if user is coming from auth
    checkNoPmUuid: () => {
      const pm_uuid = useCookies().get(COOKIES.PM_UUID)
      return !pm_uuid
    },
    checkScreeningToken: () => {
      // Has token if the user closed the screening tab
      const token = localStorage.getItem("screening-token")
      return !!token
    },
  },
}).createMachine({
  id: "screeningMachine",
  initial: "checkEntry",
  states: {
    checkEntry: {
      invoke: {
        src: "loadScreeningData",
        onDone: [
          {
            guard: ({ event }) => event.output?.type === "consent_needed",
            target: "intro",
          },
          {
            guard: ({ event }) => event.output?.type === "redirected",
            target: "final",
          },
          {
            target: "checkConditions",
          },
        ],
        onError: {
          actions: () => {
            globalActor.send({ type: "GLOBAL.SCREENING.DONE" })
          },
        },
      },
    },
    checkConditions: {
      always: [
        { target: "noPmUuid", guard: "checkNoPmUuid" },
        // We want to skip intro page if the user has already seen it so redirect to authorize
        { target: "authorize", guard: "checkGoToParam" },
        { target: "authorize", guard: "checkScreeningToken" },
        { target: "intro" },
      ],
    },
    noPmUuid: {
      entry: [
        () => {
          globalActor.send({ type: "GLOBAL.SCREENING.DONE" }) // We cannot access screening if we don't have a pm_uuid
        },
      ],
    },
    intro: {
      entry: [
        removeGotoParam,
        () => router.replace({ name: "screeningIntro" }),
      ],
      on: {
        done: { target: "authorize" },
      },
    },
    authorize: {
      entry: [
        () => deleteSessionItem(), // We can delete screening origin because the user can access the workflow
        () => router.replace({ name: "screeningAuthorize" }),
      ],
      on: {
        done: { target: "roomLoading" },
      },
    },
    roomLoading: {
      entry: [
        () => deleteSessionItem(),
        () => router.replace({ name: "screeningRoomLoading" }),
      ],
      on: {
        done: { target: "roomTest" },
        error: { target: "screeningError" },
      },
    },
    screeningError: {
      entry: [
        ({ context }) =>
          router.replace({
            name: "screeningError",
            query: context.errorSource
              ? { source: context.errorSource }
              : undefined,
          }),
      ],
    },
    roomTest: {
      entry: [
        () => deleteSessionItem(),
        () => router.replace({ name: "screeningTest" }),
      ],
      on: {
        error: { target: "screeningError" },
        done: { target: "screeningReview" },
        loadResults: { target: "screeningLoadResults" },
      },
    },
    screeningReview: {
      entry: [() => router.replace({ name: "screeningReview" })],
      on: {
        done: { target: "screeningLoadResults" },
        error: { target: "screeningError" },
      },
    },
    screeningLoadResults: {
      entry: [() => router.replace({ name: "screeningLoadResults" })],
      on: {
        error: {
          target: "screeningError",
          actions: {
            type: "setErrorSource",
            params: { source: "results" }, // Show screening error page with ?source=results for generic texts
          },
        },
        done: { target: "final" },
      },
    },
    final: {
      type: "final",
    },
  },
})

export const screeningActor = createActor(screeningMachine)

screeningActor.subscribe({
  async complete() {
    deleteSessionItem()

    const userStore = useUserStore()
    const cookies = useCookies()
    const { fetchScreeningData } = useAnswersStore()
    const { nextAssessmentName, user } = storeToRefs(userStore)
    const { logoutAndRedirectToAuth } = usePendingUser()
    const { clearScreeningStorage, clearScreeningCookies } = useAnswersStore()

    await fetchScreeningData()

    const isGuestAcc = user.value.permissions.includes("gu_acc")
    const userEmail = user.value.user.email

    if (!nextAssessmentName.value) {
      const cookiesDomain = useConfigStore().urls.cookie_domain
      const cookieOptions = { path: "/", domain: cookiesDomain }
      const cookiesToClear = [COOKIES.PM_UUID, COOKIES.COMPANY_UUID]
      cookiesToClear.forEach((cookie) => cookies.remove(cookie, cookieOptions))

      globalActor.send({ type: "GLOBAL.SCREENING.DONE" })
      return
    }

    if (nextAssessmentName.value === "meeting") {
      // Pending guest user can book meeting
      globalActor.send({
        type: `GLOBAL.REDIRECT.MEETING` as GlobalMachineEvent,
      })
      return
    }

    if (isGuestAcc) {
      // Guest user cannot pass other assessment make him register
      clearScreeningStorage()
      clearScreeningCookies()
      logoutAndRedirectToAuth(userEmail)
    } else {
      globalActor.send({
        type: `GLOBAL.REDIRECT.${nextAssessmentName.value.toUpperCase()}` as GlobalMachineEvent,
      })
    }
  },
})
